import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MarketingWebsitePageId } from '@core/seo/marketing-website-seo.models';
import { MarketingWebsiteSeoService } from '@core/seo/marketing-website-seo.service';
import { isMarketingWebsitePageId } from '@core/seo/marketing-website-seo.utils';
import { PUBLIC_BRAND_LOGO_SRC, PUBLIC_BRAND_WORDMARK } from '@core/branding/public-brand';
import {
  buildWebsiteMarketingSeoConfig,
  WebsiteMarketingCopy,
} from '../../../utils/website-marketing-seo.utils';
import { WebsiteMarketingPathService } from '../../../services/website-marketing-path.service';
import { websiteSignupHrefForFree } from '../../../utils/website-auth-urls';

@Component({
  selector: 'app-website-content-page',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './website-content-page.component.html',
  styleUrls: ['./website-content-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebsiteContentPageComponent implements OnInit {
  private readonly marketingSeo = inject(MarketingWebsiteSeoService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  readonly mktPath = inject(WebsiteMarketingPathService);

  readonly brandLogoSrc = PUBLIC_BRAND_LOGO_SRC;
  readonly brandWordmark = PUBLIC_BRAND_WORDMARK;

  get registerUrl(): string {
    return websiteSignupHrefForFree();
  }

  readonly bodyParagraphKeys = ['p1', 'p2', 'p3'] as const;
  readonly pageId = signal<MarketingWebsitePageId>('about');
  readonly i18nPrefix = signal('website.about');

  ngOnInit(): void {
    const data = this.route.snapshot.data;
    const pageId = data['pageId'];
    const prefix = data['i18nPrefix'];
    if (typeof pageId === 'string' && isMarketingWebsitePageId(pageId)) {
      this.pageId.set(pageId);
    }
    if (typeof prefix === 'string' && prefix.trim()) {
      this.i18nPrefix.set(prefix.trim());
    }

    this.applySeo();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.applySeo());
    this.destroyRef.onDestroy(() => this.marketingSeo.clear());
  }

  private applySeo(): void {
    const prefix = this.i18nPrefix();
    const copy: WebsiteMarketingCopy = {
      docTitle: this.translate.instant(`${prefix}.docTitle`),
      metaDescription: this.translate.instant(`${prefix}.metaDescription`),
      heroTitle: this.translate.instant(`${prefix}.heroTitle`),
      heroSub: this.translate.instant(`${prefix}.heroSub`),
    };
    this.marketingSeo.apply(
      buildWebsiteMarketingSeoConfig(
        this.pageId(),
        copy,
        typeof window !== 'undefined' ? window.location.origin : undefined
      )
    );
  }
}
